import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/utils/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plugs, FloppyDisk, WifiHigh, WifiSlash, ArrowClockwise, Eye, EyeSlash, LockKey } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { LiveDashboardEmbed } from "@/components/LiveDashboardEmbed";

const POLL_OPTIONS = [
  { value: "0", label: "Manual only" },
  { value: "5", label: "Every 5 seconds" },
  { value: "10", label: "Every 10 seconds" },
  { value: "15", label: "Every 15 seconds" },
  { value: "20", label: "Every 20 seconds" },
  { value: "25", label: "Every 25 seconds" },
  { value: "30", label: "Every 30 seconds" },
];

const EMPTY_CFG = {
  system_type: "", api_endpoint: "", live_endpoint: "", dashboard_url: "",
  username: "", password: "", enabled: false, poll_interval: 0,
  parking_lot_id: "", user_group_building_id: "",
};

export function InterfaceSetupTab() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const [config, setConfig] = useState(EMPTY_CFG);
  const [passwordSet, setPasswordSet] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const pollTimer = useRef(null);

  const applyConfig = (data) => {
    setConfig({
      system_type: data.system_type || "",
      api_endpoint: data.api_endpoint || "",
      live_endpoint: data.live_endpoint || "",
      dashboard_url: data.dashboard_url || "",
      username: data.username || "",
      password: "",
      enabled: !!data.enabled,
      poll_interval: Number(data.poll_interval) || 0,
      parking_lot_id: data.parking_lot_id || "",
      user_group_building_id: data.user_group_building_id || "",
    });
    setPasswordSet(!!data.password_set);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/interface/config");
        applyConfig(data);
      } catch (err) {
        console.error("Failed to load interface config:", err);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        system_type: config.system_type,
        api_endpoint: config.api_endpoint,
        live_endpoint: config.live_endpoint,
        dashboard_url: config.dashboard_url,
        username: config.username,
        enabled: config.enabled,
        poll_interval: Number(config.poll_interval) || 0,
        parking_lot_id: config.parking_lot_id,
        user_group_building_id: config.user_group_building_id,
      };
      if (config.password) payload.password = config.password;
      const { data } = await api.put("/interface/config", payload);
      applyConfig(data);
      toast.success("Interface configuration saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/interface/status");
      setStatus(data);
      if (data.connected) {
        toast.success(`Connected: ${data.status}`);
      } else {
        toast.error(`Not connected: ${data.status}${data.error ? ` — ${data.error}` : ""}`);
      }
    } catch (err) {
      setStatus({ connected: false, status: "Error", error: err.message });
      toast.error("Connection test failed");
    } finally {
      setChecking(false);
    }
  };

  // Keep pollTimer ref clean across renders (the iframe handles its own poll-reload via LiveDashboardEmbed)
  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Connection Configuration */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Plugs size={16} weight="bold" className="text-[#007AFF]" />
            API Connection Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[#737373]">Configure the external parking management system API connection (Amano PMS). Credentials are encrypted at rest.</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">System Type</Label>
              <Input value={config.system_type} onChange={(e) => setConfig({ ...config, system_type: e.target.value })}
                placeholder="e.g. Amano PMS" disabled={!isSuperadmin}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="interface-system-type" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">PMS API Endpoint</Label>
              <Input value={config.api_endpoint} onChange={(e) => setConfig({ ...config, api_endpoint: e.target.value })}
                placeholder="https://pmsapi.amano-parking.com" disabled={!isSuperadmin}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="interface-api-endpoint" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Live Monitoring Endpoint <span className="text-[#737373] normal-case tracking-normal">(separate subdomain for live entry/exit feed — used for API cross-check)</span></Label>
              <Input value={config.live_endpoint} onChange={(e) => setConfig({ ...config, live_endpoint: e.target.value })}
                placeholder="https://parkingapi.amano-parking.com" disabled={!isSuperadmin}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="interface-live-endpoint" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Dashboard URL <span className="text-[#737373] normal-case tracking-normal">(embedded below — works for local Amano server on hotel network)</span></Label>
              <Input value={config.dashboard_url} onChange={(e) => setConfig({ ...config, dashboard_url: e.target.value })}
                placeholder="http://amanoimperiallexis.ddns.net:81/" disabled={!isSuperadmin}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="interface-dashboard-url" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Username</Label>
              <Input value={config.username} onChange={(e) => setConfig({ ...config, username: e.target.value })}
                placeholder="API Username" disabled={!isSuperadmin}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="interface-username" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-1">
                <LockKey size={12} weight="bold" className="text-[#FACC15]" /> Password (Encrypted at rest)
              </Label>
              <div className="flex items-center gap-2">
                <Input value={config.password} onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  type={showPassword ? "text" : "password"}
                  placeholder={passwordSet ? "•••••••• (saved — leave blank to keep)" : "API Password"} disabled={!isSuperadmin}
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm flex-1" data-testid="interface-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="p-2 text-[#737373] hover:text-white" data-testid="interface-toggle-password">
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Parking Lot ID <span className="text-[#737373] normal-case tracking-normal">(optional filter)</span></Label>
              <Input value={config.parking_lot_id} onChange={(e) => setConfig({ ...config, parking_lot_id: e.target.value })}
                placeholder="Leave blank for all lots" disabled={!isSuperadmin}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="interface-lot-id" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Building Group ID <span className="text-[#737373] normal-case tracking-normal">(optional filter)</span></Label>
              <Input value={config.user_group_building_id} onChange={(e) => setConfig({ ...config, user_group_building_id: e.target.value })}
                placeholder="Leave blank for all buildings" disabled={!isSuperadmin}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="interface-building-id" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Live Feed Polling Interval</Label>
              <Select value={String(config.poll_interval || 0)}
                onValueChange={(v) => setConfig({ ...config, poll_interval: Number(v) })}
                disabled={!isSuperadmin}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm w-full max-w-xs" data-testid="interface-poll-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#262626] text-white">
                  {POLL_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`poll-option-${opt.value}`}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#737373]">Auto-refresh frequency for the live feed below. Choose Manual to disable polling.</p>
            </div>
          </div>
          <div className="flex items-center gap-4 pt-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={config.enabled} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                disabled={!isSuperadmin} className="accent-[#007AFF]" data-testid="interface-enabled" />
              <span className="text-sm text-[#A3A3A3]">Enable Connection</span>
            </div>
            {isSuperadmin && (
              <Button onClick={handleSave} disabled={saving}
                className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm text-xs" data-testid="interface-save-btn">
                <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving..." : "Save Configuration"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Connection Status */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
              {status?.connected ? <WifiHigh size={16} weight="bold" className="text-[#32D74B]" /> : <WifiSlash size={16} weight="bold" className="text-[#FF3B30]" />}
              Connection Status
            </CardTitle>
            <Button onClick={handleTestConnection} disabled={checking} size="sm" variant="outline"
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs" data-testid="interface-test-btn">
              <ArrowClockwise size={14} className={`mr-1 ${checking ? "animate-spin" : ""}`} /> {checking ? "Testing..." : "Test Connection"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {status ? (
            <div className={`p-4 rounded-sm border ${status.connected ? "bg-[#32D74B]/10 border-[#32D74B]/30" : "bg-[#FF3B30]/10 border-[#FF3B30]/30"}`} data-testid="interface-status-result">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${status.connected ? "bg-[#32D74B] animate-pulse" : "bg-[#FF3B30]"}`} />
                <div>
                  <p className={`text-sm font-semibold ${status.connected ? "text-[#32D74B]" : "text-[#FF3B30]"}`}>
                    {status.connected ? "Connected" : "Disconnected"}
                  </p>
                  <p className="text-xs text-[#737373]">{status.status}{status.error ? ` — ${status.error}` : ""}</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#737373]">Click &quot;Test Connection&quot; to check the API status.</p>
          )}
        </CardContent>
      </Card>

      {/* Live Amano Dashboard (iframe embed) */}
      <LiveDashboardEmbed
        url={config.dashboard_url}
        pollInterval={config.poll_interval}
        enabled={config.enabled}
      />
    </div>
  );
}
